/**
 * Adapters between HospitalisationForm's Hosp v2 body shape (camelCase,
 * sent to api-hospitalisation-v2-service today) and the transcript's
 * captured-item payload shape (snake_case, matches what the Python
 * agent emits and what Go's SaveSection fan-out reads on Save & next).
 *
 * Same pattern as complaintBodyAdapters / diagnosisBodyAdapters /
 * medicineBodyAdapters.
 *
 *   Hosp v2 add body        → captured "add" payload
 *   {cause:{icdCode, icdName, description}, status,
 *    hospitalisationDate, dischargeDate?, procedures:[{code, name, source}],
 *    description}
 *      →
 *   {name, cpt_code, cpt_description, date_of_surgery, date_of_discharge,
 *    status, description}
 *
 * Simplifications for the transcript flow:
 *   - Only ONE procedure is round-tripped as cpt_code + cpt_description
 *     (the AI's add_hospitalisation only extracts a single procedure).
 *     Multi-procedure inputs from the manual card land in the captured
 *     payload as the first procedure; extra rows get dropped on the
 *     round-trip. Reviewer keeps the full list via standalone edit.
 *   - Hosp v2's `hospitalised | discharged` enum maps to the AI's
 *     `completed | scheduled | ongoing` via the discharge-date signal
 *     (matches the Go adapter's hospStatusFromAI).
 *   - Cause.icdCode / icdName are round-tripped too when the manual
 *     card picked an ICD; the AI never sets them today.
 */

const toIsoDate = (iso) => (typeof iso === "string" && iso ? iso.slice(0, 10) : "");

const hospStatusToAi = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "discharged") return "completed";
  return "ongoing";
};

/**
 * Hosp v2 add body → captured "add" payload.
 */
export function bodyToCapturedAdd(body) {
  const cause = body.cause || {};
  const procs = Array.isArray(body.procedures) ? body.procedures : [];
  const firstProc = procs[0] || {};
  const name = cause.icdName || cause.description || "(untitled)";
  return {
    name,
    icd_code:          cause.icdCode || "",
    icd_description:   cause.icdName || "",
    cpt_code:          firstProc.code || "",
    cpt_description:   firstProc.name || "",
    date_of_surgery:   toIsoDate(body.hospitalisationDate),
    date_of_discharge: toIsoDate(body.dischargeDate),
    status:            hospStatusToAi(body.status),
    description:       body.description || "",
  };
}

/**
 * Stub — Wave A pass 1 doesn't route Hosp updates via AI. Symmetric with
 * the CC/Dx/Med adapters.
 */
export function bodyToCapturedUpdate(body /* , context */) {
  return bodyToCapturedAdd(body);
}
